/**
 * Export d'un tableau vers un fichier ouvrable dans Excel.
 *
 * DEUX CHOIX QUI COMPTENT
 *
 * 1. Separateur point-virgule et marque d'ordre des octets en tete.
 *    C'est ce qu'attend Excel en francais : avec une virgule, tout
 *    atterrit dans une seule colonne, et sans la marque UTF-8 les
 *    accents deviennent illisibles.
 *
 * 2. Les montants sortent en chiffres bruts, sans espace de milliers.
 *    A l'ecran on affiche « 1 719 000 F CFA », mais dans le fichier
 *    Excel doit voir 1719000, sinon la cellule devient du texte et
 *    aucune somme n'est possible. L'affichage et l'export ne suivent
 *    donc pas la meme regle, volontairement.
 */

export interface ExportColumn<T> {
  label: string
  /** Valeur destinee a Excel : nombre brut pour un montant. */
  value: (row: T) => string | number | null
}

function escapeCell(v: string | number | null): string {
  if (v === null || v === undefined) return ''
  const s = String(v)
  // Guillemets doubles, point-virgule ou saut de ligne : on entoure.
  if (/[";\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

export function toCsv<T>(columns: ExportColumn<T>[], rows: T[]): string {
  const lignes = [
    columns.map((c) => escapeCell(c.label)).join(';'),
    ...rows.map((r) => columns.map((c) => escapeCell(c.value(r))).join(';')),
  ]
  // \r\n : Excel sous Windows le prefere.
  return lignes.join('\r\n')
}

/** Declenche le telechargement du fichier. */
export function downloadCsv<T>(
  filename: string,
  columns: ExportColumn<T>[],
  rows: T[],
): void {
  const csv = toCsv(columns, rows)
  // ﻿ : marque d'ordre des octets, indispensable aux accents.
  const blob = new Blob(['﻿' + csv], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Libere la memoire : sans cela le fichier reste en memoire tant que
  // l'onglet est ouvert.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * PDF par la fonction d'impression du navigateur.
 *
 * Aucune bibliotheque de PDF n'est chargee ici : elle couterait une
 * centaine de kilo-octets a telecharger a Mbour pour un resultat moins
 * lisible. Sur Android, « Imprimer » propose « Enregistrer au format
 * PDF » puis le partage, donc WhatsApp.
 *
 * Les quittances et le releve mensuel, qui doivent reprendre une mise
 * en page precise, auront leur propre generateur : ce n'est pas le
 * meme besoin.
 */
export function printReport(): void {
  window.print()
}
